import { z } from 'zod';
import { RoleCode } from '../constants/index';

export const loginRequestSchema = z.object({
  login: z.string().min(3).max(254),
  password: z.string().min(1).max(128),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const userInfoSchema = z.object({
  pegawai_id: z.number(),
  nama: z.string(),
  username: z.string(),
  email: z.string().nullable(),
  roles: z.array(z.enum([RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH])),
});

export type UserInfo = z.infer<typeof userInfoSchema>;

export const loginResponseSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
  user: userInfoSchema,
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const refreshResponseSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
});

export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

export const updateMeRequestSchema = z
  .object({
    email: z.string().email().max(254).nullable().optional(),
    nomor_telepon: z.string().min(8).max(20).nullable().optional(),
    alamat: z.string().min(3).max(500).nullable().optional(),
    nama_bank: z.string().min(2).max(100).nullable().optional(),
    nomor_rekening: z.string().min(3).max(50).nullable().optional(),
    atas_nama_rekening: z.string().min(3).max(150).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Minimal satu field harus diisi',
  });

export type UpdateMeRequest = z.infer<typeof updateMeRequestSchema>;

export const changePasswordRequestSchema = z.object({
  password_lama: z.string().min(1).max(128),
  password_baru: z.string().min(8).max(128),
});

export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

export const akunInfoSchema = z.object({
  username: z.string(),
  email: z.string().nullable(),
  status_akun: z.enum(['aktif', 'nonaktif', 'terkunci']),
});

export type AkunInfo = z.infer<typeof akunInfoSchema>;

export const penugasanSekolahSchema = z.object({
  sekolah_id: z.number(),
  kode: z.string(),
  nama: z.string(),
  status_penugasan: z.enum(['aktif', 'nonaktif']),
  tanggal_mulai: z.string(),
  tanggal_selesai: z.string().nullable(),
});

export type PenugasanSekolah = z.infer<typeof penugasanSekolahSchema>;

export const meResponseSchema = z.object({
  pegawai: z.object({
    id: z.number(),
    nip_nik: z.string(),
    nama: z.string(),
    jenis_kelamin: z.enum(['L', 'P']),
    tempat_lahir: z.string().nullable(),
    tanggal_lahir: z.string().nullable(),
    nomor_telepon: z.string().nullable(),
    alamat: z.string().nullable(),
    nama_bank: z.string().nullable(),
    nomor_rekening: z.string().nullable(),
    atas_nama_rekening: z.string().nullable(),
    status_kepegawaian: z.enum(['tetap', 'kontrak', 'honorer', 'magang']),
    tanggal_bergabung: z.string(),
  }),
  akun: akunInfoSchema,
  roles: z.array(z.enum([RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH])),
  penugasan_sekolah: z.array(penugasanSekolahSchema),
});

export type MeResponse = z.infer<typeof meResponseSchema>;

export const apiSuccessSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    message: z.string(),
    data: dataSchema,
  });

export const apiErrorSchema = z.object({
  success: z.literal(false),
  message: z.string(),
  errors: z.array(
    z.object({
      field: z.string().nullable(),
      code: z.string(),
      message: z.string(),
    }),
  ),
  meta: z.object({
    request_id: z.string(),
  }),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

export const jwtPayloadSchema = z.object({
  sub: z.string(),
  account_id: z.number(),
  pid: z.number(),
  roles: z.array(z.string()),
  jti: z.string(),
  iat: z.number(),
  exp: z.number(),
  type: z.enum(['access', 'refresh']),
});

export type JwtPayload = z.infer<typeof jwtPayloadSchema>;

// ---------------------------------------------------------------------------
// Master Data (Milestone 2)
// ---------------------------------------------------------------------------

const datePattern = /^\d{4}-\d{2}-\d{2}$/;
export const dateString = z.string().regex(datePattern, 'Format tanggal YYYY-MM-DD');
export const nullableDateString = dateString.nullable();

export const nipNikSchema = z
  .string()
  .min(5, 'NIP/NIK minimal 5 karakter')
  .max(20)
  .regex(/^[A-Za-z0-9-]+$/, 'NIP/NIK hanya boleh alfanumerik dan tanda hubung');

export const nomorTeleponSchema = z
  .string()
  .min(8, 'Nomor telepon minimal 8 digit')
  .max(20)
  .regex(/^\+?[0-9-]+$/, 'Format nomor telepon tidak valid');

export const rupiahSchema = z.number().int('Harus bilangan bulat').min(0, 'Tidak boleh negatif');

// --- Sekolah ---------------------------------------------------------------
export const sekolahSchema = z.object({
  id: z.number(),
  kode: z.string(),
  nama: z.string(),
  alamat: z.string().nullable(),
  status_aktif: z.boolean(),
});

export type Sekolah = z.infer<typeof sekolahSchema>;

export const createSekolahSchema = z.object({
  kode: z
    .string()
    .min(2)
    .max(20)
    .regex(/^[A-Z0-9-]+$/, 'Kode huruf kapital/angka/strip'),
  nama: z.string().min(3).max(150),
  alamat: z.string().max(500).nullable().optional(),
});

export const updateSekolahSchema = z.object({
  nama: z.string().min(3).max(150).optional(),
  alamat: z.string().max(500).nullable().optional(),
  status_aktif: z.boolean().optional(),
});

export type CreateSekolahRequest = z.infer<typeof createSekolahSchema>;
export type UpdateSekolahRequest = z.infer<typeof updateSekolahSchema>;

// --- Role -------------------------------------------------------------------
export const roleSchema = z.object({
  id: z.number(),
  kode: z.string(),
  nama: z.string(),
  deskripsi: z.string().nullable(),
  status_aktif: z.boolean(),
});

export type Role = z.infer<typeof roleSchema>;

// --- Jabatan ----------------------------------------------------------------
export const jabatanSchema = z.object({
  id: z.number(),
  kode: z.string(),
  nama: z.string(),
  deskripsi: z.string().nullable(),
  status_aktif: z.boolean(),
});

export type Jabatan = z.infer<typeof jabatanSchema>;

export const createJabatanSchema = z.object({
  kode: z
    .string()
    .min(2)
    .max(20)
    .regex(/^[A-Z0-9_]+$/, 'Kode huruf kapital/angka/underscore'),
  nama: z.string().min(3).max(100),
  deskripsi: z.string().max(500).nullable().optional(),
});

export const updateJabatanSchema = z.object({
  nama: z.string().min(3).max(100).optional(),
  deskripsi: z.string().max(500).nullable().optional(),
  status_aktif: z.boolean().optional(),
});

export type CreateJabatanRequest = z.infer<typeof createJabatanSchema>;
export type UpdateJabatanRequest = z.infer<typeof updateJabatanSchema>;

// --- Pegawai ----------------------------------------------------------------
export const penugasanSekolahInputSchema = z.object({
  sekolah_id: z.number().int().positive(),
  tanggal_mulai: dateString,
});

export const jabatanPegawaiInputSchema = z.object({
  jabatan_id: z.number().int().positive(),
  tanggal_mulai: dateString,
});

export const akunInputSchema = z.object({
  username: z
    .string()
    .min(3)
    .max(50)
    .regex(/^[a-z0-9._-]+$/, 'Username huruf kecil/angka/titik/underscore/strip'),
  email: z.string().email().max(254).nullable().optional(),
  password: z.string().min(8).max(128),
  role_code: z.enum([RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH]),
});

export type AkunInput = z.infer<typeof akunInputSchema>;

export const createPegawaiSchema = z.object({
  nip_nik: nipNikSchema,
  nama: z.string().min(3).max(150),
  jenis_kelamin: z.enum(['L', 'P']),
  tempat_lahir: z.string().max(100).nullable().optional(),
  tanggal_lahir: dateString.nullable().optional(),
  nomor_telepon: nomorTeleponSchema.nullable().optional(),
  alamat: z.string().max(500).nullable().optional(),
  nama_bank: z.string().max(100).nullable().optional(),
  nomor_rekening: z.string().max(50).nullable().optional(),
  atas_nama_rekening: z.string().max(150).nullable().optional(),
  status_kepegawaian: z.enum(['tetap', 'kontrak', 'honorer', 'magang']),
  tanggal_bergabung: dateString,
  penugasan_sekolah: z.array(penugasanSekolahInputSchema).min(1, 'Minimal 1 sekolah'),
  jabatan: z.array(jabatanPegawaiInputSchema).min(1, 'Minimal 1 jabatan'),
  akun: akunInputSchema.nullable().optional(),
});

export const updatePegawaiSchema = z.object({
  nama: z.string().min(3).max(150).optional(),
  jenis_kelamin: z.enum(['L', 'P']).optional(),
  tempat_lahir: z.string().max(100).nullable().optional(),
  tanggal_lahir: dateString.nullable().optional(),
  nomor_telepon: nomorTeleponSchema.nullable().optional(),
  alamat: z.string().max(500).nullable().optional(),
  nama_bank: z.string().max(100).nullable().optional(),
  nomor_rekening: z.string().max(50).nullable().optional(),
  atas_nama_rekening: z.string().max(150).nullable().optional(),
  status_kepegawaian: z.enum(['tetap', 'kontrak', 'honorer', 'magang']).optional(),
  tanggal_bergabung: dateString.optional(),
  status_aktif: z.boolean().optional(),
  penugasan_sekolah: z.array(penugasanSekolahInputSchema).min(1).optional(),
  jabatan: z.array(jabatanPegawaiInputSchema).min(1).optional(),
  akun: z
    .object({
      username: z
        .string()
        .min(3)
        .max(50)
        .regex(/^[a-z0-9._-]+$/)
        .optional(),
      email: z.string().email().max(254).nullable().optional(),
      password: z.string().min(8).max(128).optional(),
      status_akun: z.enum(['aktif', 'nonaktif', 'terkunci']).optional(),
      role_code: z.enum([RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH]).optional(),
    })
    .optional(),
});

export type CreatePegawaiRequest = z.infer<typeof createPegawaiSchema>;
export type UpdatePegawaiRequest = z.infer<typeof updatePegawaiSchema>;

export const pegawaiListItemSchema = z.object({
  id: z.number(),
  nip_nik: z.string(),
  nama: z.string(),
  jenis_kelamin: z.enum(['L', 'P']),
  tempat_lahir: z.string().nullable(),
  tanggal_lahir: z.string().nullable(),
  nomor_telepon: z.string().nullable(),
  alamat: z.string().nullable(),
  nama_bank: z.string().nullable(),
  nomor_rekening: z.string().nullable(),
  atas_nama_rekening: z.string().nullable(),
  status_kepegawaian: z.enum(['tetap', 'kontrak', 'honorer', 'magang']),
  tanggal_bergabung: z.string(),
  status_aktif: z.boolean(),
  sekolah: z.array(z.object({ id: z.number(), kode: z.string(), nama: z.string() })),
  jabatan: z.array(z.object({ id: z.number(), kode: z.string(), nama: z.string() })),
});

export type PegawaiListItem = z.infer<typeof pegawaiListItemSchema>;

export const pegawaiDetailSchema = z.object({
  id: z.number(),
  nip_nik: z.string(),
  nama: z.string(),
  jenis_kelamin: z.enum(['L', 'P']),
  tempat_lahir: z.string().nullable(),
  tanggal_lahir: z.string().nullable(),
  nomor_telepon: z.string().nullable(),
  alamat: z.string().nullable(),
  nama_bank: z.string().nullable(),
  nomor_rekening: z.string().nullable(),
  atas_nama_rekening: z.string().nullable(),
  status_kepegawaian: z.enum(['tetap', 'kontrak', 'honorer', 'magang']),
  tanggal_bergabung: z.string(),
  status_aktif: z.boolean(),
  dibuat_pada: z.string(),
  diperbarui_pada: z.string(),
  akun: z
    .object({
      username: z.string(),
      email: z.string().nullable(),
      status_akun: z.enum(['aktif', 'nonaktif', 'terkunci']),
      roles: z.array(z.string()),
    })
    .nullable(),
  penugasan_sekolah: z.array(
    z.object({
      sekolah_id: z.number(),
      kode: z.string(),
      nama: z.string(),
      status_penugasan: z.enum(['aktif', 'nonaktif']),
      tanggal_mulai: z.string(),
      tanggal_selesai: z.string().nullable(),
    }),
  ),
  jabatan: z.array(
    z.object({
      jabatan_id: z.number(),
      kode: z.string(),
      nama: z.string(),
      tanggal_mulai: z.string(),
      tanggal_selesai: z.string().nullable(),
    }),
  ),
  pengaturan_honor: z
    .object({
      id: z.number(),
      tarif_honor_per_jp: z.number(),
      tarif_kehadiran_per_hari: z.number(),
      honor_jabatan: z.number(),
      honor_wali_kelas: z.number(),
      berlaku_mulai: z.string(),
      berlaku_sampai: z.string().nullable(),
    })
    .nullable(),
});

export type PegawaiDetail = z.infer<typeof pegawaiDetailSchema>;

// --- Pengaturan Honor --------------------------------------------------------
export const honorPegawaiSchema = z.object({
  id: z.number(),
  pegawai_id: z.number(),
  nip_nik: z.string().optional(),
  nama: z.string().optional(),
  tarif_honor_per_jp: rupiahSchema,
  tarif_kehadiran_per_hari: rupiahSchema,
  honor_jabatan: rupiahSchema,
  honor_wali_kelas: rupiahSchema,
  berlaku_mulai: dateString,
  berlaku_sampai: nullableDateString,
});

export const upsertHonorPegawaiSchema = z.object({
  pegawai_id: z.number().int().positive(),
  tarif_honor_per_jp: rupiahSchema,
  tarif_kehadiran_per_hari: rupiahSchema,
  honor_jabatan: rupiahSchema,
  honor_wali_kelas: rupiahSchema,
  berlaku_mulai: dateString,
  berlaku_sampai: nullableDateString.optional(),
});

export type HonorPegawai = z.infer<typeof honorPegawaiSchema>;
export type UpsertHonorPegawaiRequest = z.infer<typeof upsertHonorPegawaiSchema>;

export const createHonorPegawaiSchema = upsertHonorPegawaiSchema;
export const updateHonorPegawaiSchema = upsertHonorPegawaiSchema
  .omit({ pegawai_id: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Minimal satu field harus diisi' });

export type CreateHonorPegawaiRequest = z.infer<typeof createHonorPegawaiSchema>;
export type UpdateHonorPegawaiRequest = z.infer<typeof updateHonorPegawaiSchema>;

// --- List query --------------------------------------------------------------
export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().max(100).optional(),
  status_aktif: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  sort_by: z.string().max(50).optional(),
  sort_dir: z.enum(['asc', 'desc']).default('asc'),
});

export type ListQuery = z.infer<typeof listQuerySchema>;

export const pegawaiListQuerySchema = listQuerySchema.extend({
  sekolah_id: z.coerce.number().int().positive().optional(),
  jabatan_id: z.coerce.number().int().positive().optional(),
  status_kepegawaian: z.enum(['tetap', 'kontrak', 'honorer', 'magang']).optional(),
});

export type PegawaiListQuery = z.infer<typeof pegawaiListQuerySchema>;

// ---------------------------------------------------------------------------
// Milestone 3.1 — Periode Absensi
// ---------------------------------------------------------------------------

export const periodeSchema = z.object({
  id: z.number(),
  nama: z.string(),
  bulan: z.number().int().min(1).max(12),
  tahun: z.number().int(),
  kode_periode: z.string(),
  tanggal_mulai: z.string(),
  tanggal_selesai: z.string(),
  status: z.enum(['draft', 'aktif', 'ditutup']),
  dikunci_at: z.string().nullable(),
  dikunci_oleh_pegawai_id: z.number().nullable(),
  dibuat_pada: z.string(),
  diperbarui_pada: z.string(),
});

export type Periode = z.infer<typeof periodeSchema>;

export const createPeriodeSchema = z.object({
  nama: z.string().min(3).max(100),
  bulan: z.number().int().min(1).max(12),
  tahun: z.number().int().min(2020),
  kode_periode: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Format YYYY-MM')
    .optional(),
  tanggal_mulai: dateString,
  tanggal_selesai: dateString,
  status: z.enum(['draft', 'aktif', 'ditutup']).default('draft'),
});

export const updatePeriodeSchema = z.object({
  nama: z.string().min(3).max(100).optional(),
  status: z.enum(['draft', 'aktif', 'ditutup']).optional(),
  tanggal_mulai: dateString.optional(),
  tanggal_selesai: dateString.optional(),
});

export type CreatePeriodeRequest = z.infer<typeof createPeriodeSchema>;
export type UpdatePeriodeRequest = z.infer<typeof updatePeriodeSchema>;

// ---------------------------------------------------------------------------
// Milestone 4.1 — Periode Honor (Modul Honorarium)
// ---------------------------------------------------------------------------

export const statusPeriodeHonorEnum = z.enum(['draft', 'diproses', 'terkunci']);
export type StatusPeriodeHonor = z.infer<typeof statusPeriodeHonorEnum>;

export const periodeHonorSchema = z.object({
  id: z.number(),
  nama: z.string(),
  bulan: z.number().int().min(1).max(12),
  tahun: z.number().int(),
  kode_periode: z.string(),
  status: statusPeriodeHonorEnum,
  dikunci_at: z.string().nullable(),
  dikunci_oleh_pegawai_id: z.number().nullable(),
  dibuat_pada: z.string(),
  diperbarui_pada: z.string(),
});

export type PeriodeHonor = z.infer<typeof periodeHonorSchema>;

export const createPeriodeHonorSchema = z.object({
  nama: z.string().min(3).max(100),
  bulan: z.number().int().min(1).max(12),
  tahun: z.number().int().min(2020),
  kode_periode: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Format YYYY-MM')
    .optional(),
});

export const updatePeriodeHonorSchema = z
  .object({
    nama: z.string().min(3).max(100).optional(),
    bulan: z.number().int().min(1).max(12).optional(),
    tahun: z.number().int().min(2020).optional(),
    status: z.enum(['draft', 'diproses']).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Minimal satu field harus diisi' });

export const periodeHonorListQuerySchema = listQuerySchema.extend({
  status: z.enum(['draft', 'diproses', 'terkunci']).optional(),
});

export type CreatePeriodeHonorRequest = z.infer<typeof createPeriodeHonorSchema>;
export type UpdatePeriodeHonorRequest = z.infer<typeof updatePeriodeHonorSchema>;
export type PeriodeHonorListQuery = z.infer<typeof periodeHonorListQuerySchema>;

// ---------------------------------------------------------------------------
// Milestone 4.4 — Draft Honor
// ---------------------------------------------------------------------------

export const draftHonorSchema = z.object({
  id: z.number(),
  pegawai_id: z.number(),
  periode_honor_id: z.number(),
  total_jp: z.number().int().min(0), // M5: Input manual Admin
  jumlah_hadir: z.number().int().min(0),
  honor_kehadiran: z.number().int().min(0),
  honor_jam_mengajar: z.number().int().min(0),
  honor_jabatan: z.number().int().min(0),
  honor_wali_kelas: z.number().int().min(0),
  total_insentif: z.number().int().min(0),
  total_honor: z.number().int().min(0),
  status: z.enum(['draft', 'final']),
  dibuat_pada: z.string(),
  diperbarui_pada: z.string(),
  // Optional joined fields
  nama_pegawai: z.string().optional(),
  nip_nik: z.string().optional(),
  kode_periode: z.string().optional(),
  nama_periode: z.string().optional(),
});

export type DraftHonor = z.infer<typeof draftHonorSchema>;

export const generateDraftHonorResultSchema = z.object({
  created: z.number().int().min(0),
  periode_honor_id: z.number(),
});

export type GenerateDraftHonorResult = z.infer<typeof generateDraftHonorResultSchema>;

// ---------------------------------------------------------------------------
// Milestone 4.5 — Total JP Mengajar
// ---------------------------------------------------------------------------

export const totalJpMengajarSchema = z.object({
  id: z.number(),
  pegawai_sekolah_id: z.number(),
  periode_id: z.number(),
  total_jp: z.number().int().min(0),
  dibuat_pada: z.string(),
  diperbarui_pada: z.string(),
  pegawai_id: z.number().optional(),
  nama_pegawai: z.string().optional(),
  nip_nik: z.string().optional(),
  sekolah_id: z.number().optional(),
  nama_sekolah: z.string().optional(),
  kode_periode: z.string().optional(),
  nama_periode: z.string().optional(),
});

export type TotalJpMengajar = z.infer<typeof totalJpMengajarSchema>;

export const createTotalJpMengajarSchema = z.object({
  pegawai_sekolah_id: z.number().int().positive(),
  periode_id: z.number().int().positive(),
  total_jp: z.number().int().min(0),
});

export const updateTotalJpMengajarSchema = z.object({
  total_jp: z.number().int().min(0),
});

export const totalJpMengajarListQuerySchema = listQuerySchema.extend({
  periode_id: z.coerce.number().int().positive().optional(),
  sekolah_id: z.coerce.number().int().positive().optional(),
  pegawai_id: z.coerce.number().int().positive().optional(),
});

export type CreateTotalJpMengajarRequest = z.infer<typeof createTotalJpMengajarSchema>;
export type UpdateTotalJpMengajarRequest = z.infer<typeof updateTotalJpMengajarSchema>;
export type TotalJpMengajarListQuery = z.infer<typeof totalJpMengajarListQuerySchema>;

// ---------------------------------------------------------------------------
// Milestone 3.1 — Kalender Kerja
// ---------------------------------------------------------------------------

export const kalenderKerjaSchema = z.object({
  id: z.number(),
  sekolah_id: z.number(),
  tanggal: z.string(),
  jenis: z.enum(['hari_kerja', 'hari_libur', 'libur_nasional']),
  keterangan: z.string().nullable(),
  dibuat_pada: z.string(),
  diperbarui_pada: z.string(),
});

export type KalenderKerja = z.infer<typeof kalenderKerjaSchema>;

export const createKalenderKerjaSchema = z.object({
  sekolah_id: z.number().int().positive(),
  tanggal: dateString,
  jenis: z.enum(['hari_kerja', 'hari_libur', 'libur_nasional']),
  keterangan: z.string().max(500).nullable().optional(),
});

export const updateKalenderKerjaSchema = z.object({
  jenis: z.enum(['hari_kerja', 'hari_libur', 'libur_nasional']).optional(),
  keterangan: z.string().max(500).nullable().optional(),
});

export type CreateKalenderKerjaRequest = z.infer<typeof createKalenderKerjaSchema>;
export type UpdateKalenderKerjaRequest = z.infer<typeof updateKalenderKerjaSchema>;

// ---------------------------------------------------------------------------
// Milestone 3.2 — Absensi (Check In / Check Out)
// ---------------------------------------------------------------------------

export const statusKehadiranSchema = z.enum(['hadir', 'izin', 'sakit', 'alpha']);

export const absensiSchema = z.object({
  id: z.number(),
  pegawaiSekolahId: z.number(),
  periodeId: z.number(),
  tanggal: z.string(),
  checkInAt: z.string(),
  checkOutAt: z.string().nullable(),
  status: statusKehadiranSchema,
});

export type Absensi = z.infer<typeof absensiSchema>;

export const absensiStatusSchema = z.object({
  pegawai: z.object({
    id: z.number(),
    nama: z.string(),
    nipNik: z.string(),
  }),
  tanggal: z.string(),
  serverTime: z.string().optional(),
  periodeAktif: z
    .object({
      id: z.number(),
      nama: z.string(),
      kodePeriode: z.string(),
    })
    .nullable()
    .optional(),
  absensi: z
    .object({
      id: z.number().optional(),
      checkInAt: z.string().optional(),
      checkOutAt: z.string().nullable().optional(),
      status: statusKehadiranSchema.optional(),
      sudahCheckIn: z.boolean(),
      sudahCheckOut: z.boolean(),
    })
    .optional(),
  data: z.array(absensiSchema).optional(),
});

export type AbsensiStatus = z.infer<typeof absensiStatusSchema>;

export const updateStatusSchema = z.object({
  pegawaiSekolahId: z.number(),
  tanggal: z.string(),
  status: statusKehadiranSchema,
});

export type UpdateStatusRequest = z.infer<typeof updateStatusSchema>;

export const rekapKehadiranSchema = z.object({
  pegawaiId: z.number(),
  pegawaiSekolahId: z.number(),
  nama: z.string(),
  nipNik: z.string(),
  hadir: z.number(),
  izin: z.number(),
  sakit: z.number(),
  alpha: z.number(),
  total: z.number(),
});

export type RekapKehadiran = z.infer<typeof rekapKehadiranSchema>;

// ---------------------------------------------------------------------------
// Milestone 3.4 — Riwayat & Rekap Absensi
// ---------------------------------------------------------------------------

export const riwayatAbsensiItemSchema = z.object({
  id: z.number(),
  tanggal: z.string(),
  hari: z.string(),
  sekolahId: z.number(),
  sekolahNama: z.string(),
  pegawaiSekolahId: z.number(),
  pegawaiNama: z.string(),
  pegawaiNipNik: z.string(),
  status: statusKehadiranSchema,
  checkInAt: z.string().nullable(),
  checkOutAt: z.string().nullable(),
  keterangan: z.string().nullable(),
  periodeId: z.number(),
  periodeNama: z.string(),
});

export type RiwayatAbsensiItem = z.infer<typeof riwayatAbsensiItemSchema>;

export const riwayatAbsensiResponseSchema = z.object({
  rows: z.array(riwayatAbsensiItemSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export type RiwayatAbsensiResponse = z.infer<typeof riwayatAbsensiResponseSchema>;

export const rekapAbsensiRowSchema = z.object({
  pegawaiId: z.number(),
  pegawaiSekolahId: z.number(),
  nama: z.string(),
  nipNik: z.string(),
  sekolahNama: z.string(),
  hadir: z.number(),
  izin: z.number(),
  sakit: z.number(),
  alpha: z.number(),
  totalHariKerja: z.number(),
  persentaseKehadiran: z.number(),
});

export type RekapAbsensiRow = z.infer<typeof rekapAbsensiRowSchema>;

export const absensiDetailSchema = riwayatAbsensiItemSchema;

export type AbsensiDetail = z.infer<typeof absensiDetailSchema>;

export const institutionSettingsSchema = z.object({
  id: z.number(),
  nama_yayasan: z.string().nullable(),
  nama_pesantren: z.string(),
  logo_url: z.string().nullable(),
  alamat: z.string().nullable(),
  nomor_telepon: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
});
export const updateInstitutionSettingsSchema = institutionSettingsSchema
  .omit({ id: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Minimal satu field harus diisi' });
export type InstitutionSettings = z.infer<typeof institutionSettingsSchema>;
export type UpdateInstitutionSettingsRequest = z.infer<typeof updateInstitutionSettingsSchema>;
export const insentifKegiatanSchema = z.object({
  id: z.number(),
  pegawai_sekolah_id: z.number(),
  periode_id: z.number(),
  nama_kegiatan: z.string(),
  tanggal_kegiatan: z.string(),
  nominal: rupiahSchema,
  keterangan: z.string().nullable().optional(),
  dibuat_pada: z.string().optional(),
  diperbarui_pada: z.string().optional(),
  pegawai_id: z.number().optional(),
  nama_pegawai: z.string().optional(),
  nip_nik: z.string().optional(),
  sekolah_id: z.number().optional(),
  nama_sekolah: z.string().optional(),
  kode_periode: z.string().optional(),
  nama_periode: z.string().optional(),
});

export const createInsentifKegiatanSchema = z.object({
  pegawai_sekolah_id: z.number().int().positive(),
  periode_id: z.number().int().positive(),
  nama_kegiatan: z.string().min(1).max(255),
  tanggal_kegiatan: dateString,
  nominal: rupiahSchema,
  keterangan: z.string().max(500).nullable().optional(),
});

export const updateInsentifKegiatanSchema = z.object({
  nama_kegiatan: z.string().min(1).max(255).optional(),
  tanggal_kegiatan: dateString.optional(),
  nominal: rupiahSchema.optional(),
  keterangan: z.string().max(500).nullable().optional(),
});

export const insentifKegiatanListQuerySchema = listQuerySchema.extend({
  periode_id: z.coerce.number().int().positive().optional(),
  sekolah_id: z.coerce.number().int().positive().optional(),
  pegawai_id: z.coerce.number().int().positive().optional(),
});

export type InsentifKegiatan = z.infer<typeof insentifKegiatanSchema>;
export type CreateInsentifKegiatanRequest = z.infer<typeof createInsentifKegiatanSchema>;
export type UpdateInsentifKegiatanRequest = z.infer<typeof updateInsentifKegiatanSchema>;
export type InsentifKegiatanListQuery = z.infer<typeof insentifKegiatanListQuerySchema>;

// ---------------------------------------------------------------------------
// Honorarium — Slip (M4.3)
// ---------------------------------------------------------------------------

export const insentifSlipSchema = z.object({
  nama_kegiatan: z.string(),
  nominal: rupiahSchema,
});

export type InsentifSlip = z.infer<typeof insentifSlipSchema>;

export const slipHonorariumSchema = z.object({
  id: z.number(),
  pegawai_id: z.number(),
  pegawai_nama: z.string(),
  pegawai_nip_nik: z.string(),
  sekolah_id: z.number(),
  sekolah_nama: z.string(),
  jabatan_nama: z.string(),
  periode_honor_id: z.number(),
  kode_periode: z.string(),
  status_slip: z.enum(['draft', 'final']),
  total_jp: z.number(),
  tarif_per_jp: rupiahSchema,
  honor_jp: rupiahSchema,
  hari_hadir: z.number(),
  tarif_kehadiran: rupiahSchema,
  honor_kehadiran: rupiahSchema,
  honor_jabatan: rupiahSchema,
  honor_wali_kelas: rupiahSchema,
  insentif: z.array(insentifSlipSchema),
  total_honor: rupiahSchema,
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export type SlipHonorarium = z.infer<typeof slipHonorariumSchema>;

export const slipHonorariumListQuerySchema = listQuerySchema.extend({
  periode_id: z
    .string()
    .optional()
    .transform((v) => (v ? Number(v) : undefined)),
  sekolah_id: z
    .string()
    .optional()
    .transform((v) => (v ? Number(v) : undefined)),
  pegawai_id: z
    .string()
    .optional()
    .transform((v) => (v ? Number(v) : undefined)),
  status_slip: z.enum(['draft', 'final']).optional(),
});

export type SlipHonorariumListQuery = z.infer<typeof slipHonorariumListQuerySchema>;

// ---------------------------------------------------------------------------
// Milestone 7.2 — Laporan Absensi dengan Filter
// ---------------------------------------------------------------------------

export const laporanAbsensiRowSchema = z.object({
  pegawaiId: z.number(),
  pegawaiSekolahId: z.number(),
  nama: z.string(),
  nipNik: z.string(),
  sekolahNama: z.string(),
  sekolahId: z.number(),
  hadir: z.number(),
  izin: z.number(),
  sakit: z.number(),
  alpha: z.number(),
  totalHariKerja: z.number(),
  persentaseKehadiran: z.number(),
});

export type LaporanAbsensiRow = z.infer<typeof laporanAbsensiRowSchema>;

export const laporanAbsensiResponseSchema = z.object({
  rows: z.array(laporanAbsensiRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export type LaporanAbsensiResponse = z.infer<typeof laporanAbsensiResponseSchema>;

export const laporanAbsensiQuerySchema = z.object({
  periodeId: z.coerce.number().int().positive(),
  sekolahId: z.coerce.number().int().positive().optional(),
  pegawaiId: z.coerce.number().int().positive().optional(),
  status: statusKehadiranSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().max(100).optional(),
  sort_by: z
    .enum(['nama', 'nipNik', 'sekolahNama', 'hadir', 'persentaseKehadiran'])
    .default('nama'),
  sort_dir: z.enum(['asc', 'desc']).default('asc'),
});

export type LaporanAbsensiQuery = z.infer<typeof laporanAbsensiQuerySchema>;

// ---------------------------------------------------------------------------
// Milestone 7.3 — Admin Account Management (Reset Password & Unlock)
// ---------------------------------------------------------------------------

export const adminAkunListItemSchema = z.object({
  id: z.number(),
  pegawai_id: z.number(),
  nip_nik: z.string(),
  nama: z.string(),
  username: z.string(),
  email: z.string().nullable(),
  status_akun: z.enum(['aktif', 'nonaktif', 'terkunci']),
  roles: z.array(z.string()),
  terakhir_login_at: z.string().nullable(),
  dibuat_pada: z.string(),
});

export type AdminAkunListItem = z.infer<typeof adminAkunListItemSchema>;

export const adminAkunResetPasswordSchema = z.object({
  password: z.string().min(8).max(128),
});

export type AdminAkunResetPasswordRequest = z.infer<typeof adminAkunResetPasswordSchema>;

export const registerOptionsSchema = z.object({
  sekolah: z.array(z.object({ id: z.number(), nama: z.string() })),
  jabatan: z.array(z.object({ id: z.number(), nama: z.string() })),
});

export type RegisterOptions = z.infer<typeof registerOptionsSchema>;

export const registerRequestSchema = z
  .object({
    nama: z.string().min(3).max(150),
    email: z.string().email().max(254),
    password: z.string().min(8).max(128),
    konfirmasi_password: z.string().min(8).max(128),
    nomor_telepon: nomorTeleponSchema,
    jenis_kelamin: z.enum(['L', 'P']),
    sekolah_id: z.number().int().positive(),
    jabatan_id: z.number().int().positive(),
    role_code: z.enum([RoleCode.GURU, RoleCode.KEPALA_SEKOLAH]),
  })
  .refine((v) => v.password === v.konfirmasi_password, {
    message: 'Konfirmasi password tidak sama',
    path: ['konfirmasi_password'],
  });

export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const pendingUserListItemSchema = z.object({
  id: z.number(),
  pegawai_id: z.number(),
  nama: z.string(),
  email: z.string().nullable(),
  sekolah: z.string(),
  jabatan: z.string(),
  role_code: z.string(),
  dibuat_pada: z.string(),
  verification_status: z.enum(['pending', 'approved', 'rejected']),
  verification_reason: z.string().nullable(),
});

export type PendingUserListItem = z.infer<typeof pendingUserListItemSchema>;

export const adminAkunListQuerySchema = listQuerySchema.extend({
  status_akun: z.enum(['aktif', 'nonaktif', 'terkunci']).optional(),
  role: z.enum([RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH]).optional(),
});

export type AdminAkunListQuery = z.infer<typeof adminAkunListQuerySchema>;

export const adminAkunUnlockSchema = z.object({
  password: z.string().min(8).max(128),
});

export type AdminAkunUnlockRequest = z.infer<typeof adminAkunUnlockSchema>;

export const adminAkunDeactivateSchema = z.object({
  alasan: z.string().min(3).max(500),
});

export type AdminAkunDeactivateRequest = z.infer<typeof adminAkunDeactivateSchema>;

// ---------------------------------------------------------------------------
// Milestone 7.2 — Audit Log
// ---------------------------------------------------------------------------

export const auditLogItemSchema = z.object({
  id: z.number(),
  aksi: z.enum([
    'create',
    'update',
    'delete',
    'lock',
    'unlock',
    'finalize',
    'adjust',
    'login',
    'logout',
  ]),
  entitas: z.string(),
  entitas_id: z.string().nullable(),
  pegawai_id: z.number().nullable(),
  pegawai_nama: z.string().nullable(),
  alasan: z.string().nullable(),
  detail: z.string().nullable(),
  dibuat_pada: z.string(),
});

export type AuditLogItem = z.infer<typeof auditLogItemSchema>;

export const auditLogListQuerySchema = listQuerySchema.extend({
  aksi: z
    .enum(['create', 'update', 'delete', 'lock', 'unlock', 'finalize', 'adjust', 'login', 'logout'])
    .optional(),
  entitas: z.string().optional(),
  pegawai_id: z.coerce.number().int().positive().optional(),
  tanggal_dari: z.string().optional(),
  tanggal_sampai: z.string().optional(),
});

export type AuditLogListQuery = z.infer<typeof auditLogListQuerySchema>;

// ---------------------------------------------------------------------------
// Milestone 7.3 — Role Assignment
// ---------------------------------------------------------------------------

export const pegawaiRoleAssignmentSchema = z.object({
  pegawai_id: z.number(),
  role_id: z.number(),
  tanggal_mulai: z.string().min(1, 'Tanggal mulai wajib diisi'),
  tanggal_selesai: z.string().nullable().optional(),
});

export type PegawaiRoleAssignmentRequest = z.infer<typeof pegawaiRoleAssignmentSchema>;

export const pegawaiRoleRowSchema = z.object({
  id: z.number(),
  pegawai_id: z.number(),
  role_id: z.number(),
  role_kode: z.string(),
  role_nama: z.string(),
  tanggal_mulai: z.string(),
  tanggal_selesai: z.string().nullable(),
  dibuat_pada: z.string(),
});

export type PegawaiRoleRow = z.infer<typeof pegawaiRoleRowSchema>;

// ---------------------------------------------------------------------------
// Milestone 7.4 — System Settings
// ---------------------------------------------------------------------------

export const systemSettingSchema = z.object({
  id: z.number(),
  kunci: z.string(),
  nilai: z.string().nullable(),
  deskripsi: z.string().nullable(),
  diperbarui_pada: z.string().nullable(),
});

export type SystemSetting = z.infer<typeof systemSettingSchema>;

export const updateSystemSettingSchema = z.object({
  nilai: z.string().nullable(),
  konfirmasi: z
    .boolean()
    .refine((v) => v === true, { message: 'Konfirmasi perubahan wajib dicentang' }),
  alasan: z.string().min(3, 'Alasan minimal 3 karakter').max(500),
});

export type UpdateSystemSettingRequest = z.infer<typeof updateSystemSettingSchema>;

// ---------------------------------------------------------------------------
// Milestone 5.3 — Laporan Honorarium dengan Filter
// ---------------------------------------------------------------------------

export const laporanHonorariumRowSchema = z.object({
  pegawai_id: z.number(),
  nip_nik: z.string(),
  nama: z.string(),
  sekolah_id: z.number(),
  sekolah_nama: z.string(),
  jabatan_nama: z.string(),
  kode_periode: z.string(),
  status_slip: z.enum(['draft', 'final']),
  total_jp: z.number(),
  honor_jp: rupiahSchema,
  hari_hadir: z.number(),
  honor_kehadiran: rupiahSchema,
  honor_jabatan: rupiahSchema,
  honor_wali_kelas: rupiahSchema,
  total_insentif: rupiahSchema,
  total_honor: rupiahSchema,
});

export type LaporanHonorariumRow = z.infer<typeof laporanHonorariumRowSchema>;

export const laporanHonorariumResponseSchema = z.object({
  rows: z.array(laporanHonorariumRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  grand_total: z.object({
    honor_jp: rupiahSchema,
    honor_kehadiran: rupiahSchema,
    honor_jabatan: rupiahSchema,
    honor_wali_kelas: rupiahSchema,
    total_insentif: rupiahSchema,
    total_honor: rupiahSchema,
    jumlah_pegawai: z.number(),
  }),
});

export type LaporanHonorariumResponse = z.infer<typeof laporanHonorariumResponseSchema>;

export const laporanHonorariumQuerySchema = z.object({
  periode_honor_id: z.coerce.number().int().positive(),
  sekolah_id: z.coerce.number().int().positive().optional(),
  pegawai_id: z.coerce.number().int().positive().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().max(100).optional(),
  sort_by: z
    .enum([
      'nama',
      'nip_nik',
      'sekolah_nama',
      'honor_jp',
      'honor_kehadiran',
      'honor_jabatan',
      'honor_wali_kelas',
      'total_insentif',
      'total_honor',
    ])
    .default('nama'),
  sort_dir: z.enum(['asc', 'desc']).default('asc'),
});

export type LaporanHonorariumQuery = z.infer<typeof laporanHonorariumQuerySchema>;

// ---------------------------------------------------------------------------
// Milestone 8 — Jenis Komponen Honor (Master)
// ---------------------------------------------------------------------------

export const jenisKomponenHonorSchema = z.object({
  id: z.number(),
  kode: z.string(),
  nama: z.string(),
  deskripsi: z.string().nullable(),
  urutan: z.number().int().min(0),
  status_aktif: z.boolean(),
  dibuat_pada: z.string(),
  diperbarui_pada: z.string(),
});

export type JenisKomponenHonor = z.infer<typeof jenisKomponenHonorSchema>;

export const createJenisKomponenHonorSchema = z.object({
  kode: z
    .string()
    .min(2)
    .max(50)
    .regex(/^[a-z0-9_]+$/, 'Kode huruf kecil/angka/underscore'),
  nama: z.string().min(3).max(100),
  deskripsi: z.string().max(500).nullable().optional(),
  urutan: z.number().int().min(0).optional(),
});

export const updateJenisKomponenHonorSchema = z.object({
  nama: z.string().min(3).max(100).optional(),
  deskripsi: z.string().max(500).nullable().optional(),
  urutan: z.number().int().min(0).optional(),
  status_aktif: z.boolean().optional(),
});

export type CreateJenisKomponenHonorRequest = z.infer<typeof createJenisKomponenHonorSchema>;
export type UpdateJenisKomponenHonorRequest = z.infer<typeof updateJenisKomponenHonorSchema>;

// ---------------------------------------------------------------------------
// Milestone 8 — Konfigurasi Honor Jabatan (Temporal)
// ---------------------------------------------------------------------------

export const konfigurasiHonorJabatanSchema = z.object({
  id: z.number(),
  jabatan_id: z.number(),
  honor_jabatan: rupiahSchema,
  berlaku_mulai: dateString,
  berlaku_sampai: nullableDateString,
  dibuat_pada: z.string(),
  diperbarui_pada: z.string(),
  // Optional joined fields
  jabatan_kode: z.string().optional(),
  jabatan_nama: z.string().optional(),
});

export type KonfigurasiHonorJabatan = z.infer<typeof konfigurasiHonorJabatanSchema>;

export const upsertKonfigurasiHonorJabatanSchema = z.object({
  jabatan_id: z.number().int().positive(),
  honor_jabatan: rupiahSchema,
  berlaku_mulai: dateString,
  berlaku_sampai: nullableDateString.optional(),
});

export const createKonfigurasiHonorJabatanSchema = upsertKonfigurasiHonorJabatanSchema;
export const updateKonfigurasiHonorJabatanSchema = upsertKonfigurasiHonorJabatanSchema
  .omit({ jabatan_id: true, berlaku_mulai: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Minimal satu field harus diisi' });

export type CreateKonfigurasiHonorJabatanRequest = z.infer<
  typeof createKonfigurasiHonorJabatanSchema
>;
export type UpdateKonfigurasiHonorJabatanRequest = z.infer<
  typeof updateKonfigurasiHonorJabatanSchema
>;

// ---------------------------------------------------------------------------
// Milestone 8 — Penyesuaian Honor (Adjustment Audit)
// ---------------------------------------------------------------------------

export const penyesuaianHonorSchema = z.object({
  id: z.number(),
  draft_honor_id: z.number(),
  pegawai_id: z.number(),
  periode_honor_id: z.number(),
  alasan: z.string(),
  before_json: z.string(),
  after_json: z.string(),
  dibuat_pada: z.string(),
  // Optional joined fields
  pegawai_nama: z.string().optional(),
  pegawai_nip_nik: z.string().optional(),
});

export type PenyesuaianHonor = z.infer<typeof penyesuaianHonorSchema>;

export const createPenyesuaianHonorSchema = z.object({
  draft_honor_id: z.number().int().positive(),
  pegawai_id: z.number().int().positive(),
  periode_honor_id: z.number().int().positive(),
  alasan: z.string().min(3).max(500),
});

export type CreatePenyesuaianHonorRequest = z.infer<typeof createPenyesuaianHonorSchema>;

// ---------------------------------------------------------------------------
// Milestone 8 — Honor Bulanan (Snapshot Ledger)
// ---------------------------------------------------------------------------

export const honorBulananSchema = z.object({
  id: z.number(),
  pegawai_id: z.number(),
  periode_honor_id: z.number(),
  total_jp: z.number().int().min(0),
  jumlah_hadir: z.number().int().min(0),
  total_honor: rupiahSchema,
  status: z.enum(['final']),
  finalisasi_at: z.string(),
  finalisasi_oleh_pegawai_id: z.number().nullable(),
  dibuat_pada: z.string(),
  // Optional joined fields
  pegawai_nama: z.string().optional(),
  pegawai_nip_nik: z.string().optional(),
  kode_periode: z.string().optional(),
  detail: z
    .array(
      z.object({
        id: z.number(),
        jenis_komponen_honor_id: z.number(),
        kode: z.string(),
        nama: z.string(),
        nominal: z.number(),
        keterangan: z.string().nullable(),
      }),
    )
    .optional(),
});

export type HonorBulanan = z.infer<typeof honorBulananSchema>;

export const honorBulananListQuerySchema = listQuerySchema.extend({
  periode_honor_id: z.coerce.number().int().positive().optional(),
  pegawai_id: z.coerce.number().int().positive().optional(),
});

export type HonorBulananListQuery = z.infer<typeof honorBulananListQuerySchema>;

export const finalisasiHonorResultSchema = z.object({
  created: z.number().int().min(0),
  periode_honor_id: z.number(),
});

export type FinalisasiHonorResult = z.infer<typeof finalisasiHonorResultSchema>;
